import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
  Event,
  MassText,
  MassTextAttachment,
  MassTextType,
  ScheduleTextInfo,
  SubSelectionItem,
  SubSelectionType,
} from "@markit/common.types";
import { getAccountState } from "../../../redux/slices/accountSlice";
import MassTextMessageComposition from "./Summary/MassTextMessageComposition";
import MassTextsSummary from "./Summary/MassTextsSummary";
import MassTextsSendSchedule from "./Summary/MassTextsSendSchedule";
import MassTextsRecipients from "./MassTextsRecipients";
import MassTextsMessagingHeader from "./MassTextsMessagingHeader";
import {
  MassTextMessagingScreenType,
  MassTextsSharedProps,
} from "./MassTextPanel";
import isEqual from "lodash.isequal";
import { API } from "../../../API";
import {
  calculateTextMessageCreditsEstimate,
  isDefaultTemplateAutomations,
  isDelayedTriggerAutomation,
  isInitialTriggerText,
  LessThanDate,
  massTextMessageConverter,
  massTextPlaceholder,
  massTextUpcomingEventPlaceholder,
  NON_SUBSCRIBER_REPLY_CAMPAIGN_ID,
  ONE_DAY_IN_MS,
} from "@markit/common.utils";
import { GetLongDate } from "../../../utils/GetLongDate";
import { useTheme } from "../../../hooks/useTheme";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import MassTextBodyWrapper from "./MassTextBodyWrapper";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { MassTextsWrapperType } from "./MassTextWrapperManager";
import { fetchNumFollowersNoComplianceSent } from "../../../utils/userUtils";
import {
  appendedComplianceMessage,
  getMassTextAttachments,
} from "../../../utils/textingUtils";

type MassTextMessagingScreensProps = MassTextsSharedProps & {
  setEditingCampaign?: (editingCampaign: boolean) => void;
  duplicateOnPress?: (massText: MassText) => void;
  isEditing: boolean;
  event: Event | undefined;
  netSelectedPeople: string[];
  editingMassText: MassText | undefined;
  resetState: (newMassText?: MassText) => void;
  suggestedAction: SubSelectionItem | undefined;
  loadingRecipients: boolean;
};

const MassTextMessagingScreens = (props: MassTextMessagingScreensProps) => {
  const { theme } = useTheme();
  const { account } = useSelector(getAccountState);
  const { accountData, followersData } = account;
  const {
    wrapperType,
    campaign,
    setEditingCampaign,
    duplicateOnPress,
    isEditing,
    event,
    massTextSettings,
    updateMassTextSettings,
    netSelectedPeople,
    setProfileSelected,
    editingMassText,
    suggestedAction,
    setMainScreen,
    resetState,
    loadingRecipients,
  } = props;

  const [screen, setScreen] = useState<MassTextMessagingScreenType>(
    MassTextMessagingScreenType.SUMMARY
  );
  // Local state for the Sending_Schedule screen
  const [scheduleTextInfo, setScheduleTextInfo] = useState<ScheduleTextInfo>({
    sentAt: "",
    scheduleToggle: false,
    catchUpChecked: false,
    sentAtOffset: 0,
  });
  const [totalDailyMessages, setTotalDailyMessages] = useState(0);
  const [loadingDailyMessages, setLoadingDailyMessages] = useState(true);
  const [categoryRecipients, setCategoryRecipients] = useState<string[]>([]);
  const [loadingCategoryRecipients, setLoadingCategoryRecipients] =
    useState(true);

  const [massTextAttachments, setMassTextAttachments] = useState<
    MassTextAttachment[] | undefined
  >();

  useEffect(() => {
    if (editingMassText && isEditing) {
      setScreen(MassTextMessagingScreenType.SEND_SUMMARY);
    } else {
      setScreen(MassTextMessagingScreenType.SUMMARY);
    }
  }, [editingMassText, isEditing]);

  // Should probably move this somewhere else to one main fetching location and utilize the useLiveUpdatingEventTexts hook
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (!massTextAttachments && massTextSettings.sentBy !== "") {
      if (isEditing) {
        const attachments = await getMassTextAttachments(
          massTextSettings.sentBy,
          massTextSettings
        );
        setMassTextAttachments(attachments);
      } else {
        setMassTextAttachments([]);
      }
    }
  }, [isEditing, massTextSettings, massTextAttachments]);

  const isEditingScheduledText = useMemo(
    () =>
      (LessThanDate(new Date().toISOString(), massTextSettings.sentAt) ||
        massTextSettings.type === MassTextType.TRIGGERTEXT) &&
      isEditing,
    [isEditing, massTextSettings.sentAt, massTextSettings.type]
  );

  const defaultMessage = useMemo(
    () =>
      event && suggestedAction?.type === SubSelectionType.ALL_FOLLOWERS
        ? massTextUpcomingEventPlaceholder(
            accountData.fullName,
            GetLongDate(event.start, false, true, true, event.timezone),
            event.id,
            accountData.trustedSender
          )
        : massTextPlaceholder(accountData.fullName),
    [accountData.fullName, event, suggestedAction, accountData.trustedSender]
  );

  // Returns the net categories from the selected and excluded categories
  const finalSubSelectionIds = useMemo(
    () =>
      suggestedAction
        ? [suggestedAction]
        : massTextSettings.subSelectionIds || [],
    [massTextSettings.subSelectionIds, suggestedAction]
  );

  // Displays the FINAL recipients whether it's for categories or for individuals
  const finalRecipients = useMemo(
    () =>
      finalSubSelectionIds.length > 0 && campaign?.eventId
        ? categoryRecipients
        : netSelectedPeople,
    [
      finalSubSelectionIds.length,
      campaign?.eventId,
      categoryRecipients,
      netSelectedPeople,
    ]
  );

  const numFollowersNotCompliant = useMemo(
    () => fetchNumFollowersNoComplianceSent(followersData, finalRecipients),
    [followersData, finalRecipients]
  );

  // Case where if >50% of recipients have not received a compliance message
  const showComplianceCreditNotice = useMemo(
    () => numFollowersNotCompliant > finalRecipients.length * 0.5,
    [numFollowersNotCompliant, finalRecipients]
  );

  const convertedMessage = useMemo(
    () =>
      massTextMessageConverter(
        massTextSettings.message,
        massTextAttachments,
        accountData.trustedSender
      ),
    [massTextSettings.message, massTextAttachments, accountData.trustedSender]
  );

  const finalMessageWithAppendages = useMemo(
    () =>
      convertedMessage +
      (massTextSettings.campaignId === NON_SUBSCRIBER_REPLY_CAMPAIGN_ID
        ? `dddddddddddddddd/u/${accountData.username}/subscribe` // placeholder for M4RKT subscribeProfileDefault num characters
        : isInitialTriggerText(massTextSettings, campaign?.initialTextId) ||
          showComplianceCreditNotice
        ? appendedComplianceMessage
        : ""),
    [
      accountData.username,
      campaign?.initialTextId,
      convertedMessage,
      massTextSettings,
      showComplianceCreditNotice,
    ]
  );

  const numCredits = useMemo(
    () =>
      calculateTextMessageCreditsEstimate(
        finalMessageWithAppendages,
        accountData.trustedSender,
        massTextSettings.mediaUrl,
        event
      ),
    [
      accountData.trustedSender,
      event,
      massTextSettings.mediaUrl,
      finalMessageWithAppendages,
    ]
  );

  /* Special case variables for mass texts when only some recipients that need compliance sent.
     This is used to be the opposite of the finalMessageWithAppendages calculate num credits of
     the text with or without compliance message. */
  const finalMessageWithAppendagesOppositeCompliance = useMemo(
    () =>
      showComplianceCreditNotice
        ? convertedMessage
        : convertedMessage + appendedComplianceMessage,
    [convertedMessage, showComplianceCreditNotice]
  );

  const numCreditsOppositeCompliance = useMemo(
    () =>
      calculateTextMessageCreditsEstimate(
        finalMessageWithAppendagesOppositeCompliance,
        accountData.trustedSender,
        massTextSettings.mediaUrl,
        event
      ),
    [
      accountData.trustedSender,
      event,
      massTextSettings.mediaUrl,
      finalMessageWithAppendagesOppositeCompliance,
    ]
  );
  /* End of special case functions */

  const sharedSummaryPropsPack = {
    campaign: campaign,
    finalRecipients: finalRecipients,
    netIndividualRecipients: netSelectedPeople,
    massText: massTextSettings,
    updateMassTextSettings: updateMassTextSettings,
    scheduleTextInfo: scheduleTextInfo,
    setScheduleTextInfo: setScheduleTextInfo,
    numCredits: numCredits,
    numFollowersNotCompliant: numFollowersNotCompliant,
    showComplianceCreditNotice: showComplianceCreditNotice,
    convertedMessage: convertedMessage,
    finalMessageWithAppendages: finalMessageWithAppendages,
    massTextAttachments: massTextAttachments,
    screen: screen,
    setScreen: setScreen,
    setMainScreen: setMainScreen,
    isEditing: isEditing,
    wrapperType: wrapperType,
    suggestedAction: suggestedAction,
    loadingRecipients: loadingRecipients,
    theme: theme,
  };

  useAsyncOnMount(async () => {
    // Set the default scheduleTextInfo if event automation
    if (
      isDefaultTemplateAutomations(massTextSettings.campaignId) ||
      (campaign && isDelayedTriggerAutomation(campaign, event))
    ) {
      setScheduleTextInfo({
        sentAt: "",
        scheduleToggle: massTextSettings.scheduled,
        catchUpChecked: massTextSettings.catchUpEnabled,
        sentAtOffset: isEditing
          ? massTextSettings.sentAtOffset
          : campaign && campaign.id === DEFAULT_EVENT_TEXTS_CAMPAIGN_ID
          ? -ONE_DAY_IN_MS
          : ONE_DAY_IN_MS,
      });
    } else {
      setScheduleTextInfo({
        sentAt: massTextSettings.sentAt,
        scheduleToggle: massTextSettings.scheduled,
        catchUpChecked: massTextSettings.catchUpEnabled,
        sentAtOffset: 0,
      });
    }

    // Fetch daily message count if not a subaccount and sends within main twilio account
    // Subaccounts we can ignore and just return 0
    if (!accountData.customer.subAccountSid) {
      const { totalMessages } =
        await API.monitor.checkTotalDailyTwilioMessagesSent({});
      setTotalDailyMessages(totalMessages);
    }
    setLoadingDailyMessages(false);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    // Fetch the recipients from the categories to display the number it will be sent to
    if (event && finalSubSelectionIds.length > 0) {
      setLoadingCategoryRecipients(true);
      const { recipients } = await API.text.convertTextCategories({
        selectedCategories: finalSubSelectionIds,
        excludedCategories: massTextSettings.excludedSubSelectionIds,
        userId: accountData.uid,
        eventId: event.id,
      });
      setCategoryRecipients(recipients);
    }
    setLoadingCategoryRecipients(false);
  }, [
    event,
    accountData.uid,
    finalSubSelectionIds,
    massTextSettings.excludedSubSelectionIds,
  ]);

  const madeChanges = useMemo(() => {
    if (screen === MassTextMessagingScreenType.SUMMARY) {
      return (
        (!suggestedAction && finalRecipients.length > 0) ||
        massTextSettings.message !== defaultMessage ||
        massTextSettings.mediaUrl !== ""
      );
    } else if (screen === MassTextMessagingScreenType.SEND_SUMMARY) {
      return isEditing && !isEqual(massTextSettings, editingMassText);
    } else if (screen === MassTextMessagingScreenType.SEND_SCHEDULE) {
      return !isEqual(
        {
          scheduled: massTextSettings.scheduled,
          catchUpEnabled: massTextSettings.catchUpEnabled,
          sentAt: massTextSettings.sentAt,
          sentAtOffset: massTextSettings.sentAtOffset,
        },
        {
          scheduled: scheduleTextInfo.scheduleToggle,
          catchUpEnabled: scheduleTextInfo.catchUpChecked,
          sentAt: scheduleTextInfo.sentAt,
          sentAtOffset: scheduleTextInfo.sentAtOffset,
        }
      );
    }
    return false;
  }, [
    defaultMessage,
    editingMassText,
    finalRecipients.length,
    isEditing,
    massTextSettings,
    scheduleTextInfo,
    screen,
    suggestedAction,
  ]);

  return (
    <div className="ColumnNormal PreventScroll">
      <MassTextsMessagingHeader
        wrapperType={wrapperType}
        campaign={campaign}
        setEditingCampaign={setEditingCampaign}
        duplicateOnPress={duplicateOnPress}
        screen={screen}
        setScreen={setScreen}
        massText={massTextSettings}
        updateMassTextSettings={updateMassTextSettings}
        numCredits={numCredits}
        numCreditsOppositeCompliance={numCreditsOppositeCompliance}
        numFollowersNotCompliant={numFollowersNotCompliant}
        showComplianceCreditNotice={showComplianceCreditNotice}
        resetState={resetState}
        madeChanges={madeChanges}
        finalRecipients={finalRecipients}
        suggestedAction={suggestedAction}
        totalDailyMessages={totalDailyMessages}
        loadingDailyMessages={loadingDailyMessages}
        loadingCategoryRecipients={loadingCategoryRecipients}
        scheduleTextInfo={scheduleTextInfo}
        setScheduleTextInfo={setScheduleTextInfo}
        isEditing={isEditing}
      />
      <HorizontalDivider />
      <MassTextBodyWrapper wrapperType={wrapperType}>
        {screen === MassTextMessagingScreenType.SUMMARY ? (
          // Case 1: New Mass Text Drafting Screen
          <MassTextMessageComposition
            {...sharedSummaryPropsPack}
            event={event}
          />
        ) : wrapperType === MassTextsWrapperType.SIDE_PANEL &&
          isEditingScheduledText &&
          (screen === MassTextMessagingScreenType.SEND_SUMMARY ||
            screen === MassTextMessagingScreenType.LINK_ANALYTICS) ? (
          // Case 2: Editing Scheduled Text Screen
          <MassTextMessageComposition
            {...sharedSummaryPropsPack}
            event={event}
          />
        ) : screen === MassTextMessagingScreenType.SEND_SUMMARY ||
          screen === MassTextMessagingScreenType.LINK_ANALYTICS ? (
          // Case 3: Sent Mass Text Summary Screen
          <MassTextsSummary {...sharedSummaryPropsPack} />
        ) : null}
        {/* Mass Texts Send Schedule Screen */}
        {screen === MassTextMessagingScreenType.SEND_SCHEDULE ? (
          <MassTextsSendSchedule
            massText={massTextSettings}
            scheduleTextInfo={scheduleTextInfo}
            setScheduleTextInfo={setScheduleTextInfo}
            isEditing={isEditing}
          />
        ) : null}
        {/* Mass Text Recipients List */}
        {screen === MassTextMessagingScreenType.RECIPIENTS_SEND_SUMMARY ? (
          <MassTextsRecipients
            campaign={campaign}
            massText={massTextSettings}
            setProfileSelected={setProfileSelected}
          />
        ) : null}
      </MassTextBodyWrapper>
    </div>
  );
};

export default MassTextMessagingScreens;
